import { BarangayTabulation, Candidate, MunicipalCandidate, ProvincialCandidate, Tabulation, User } from "../../types";
import { Box, Grid, Typography } from "@mui/material";
import { collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";

import CandidateComparison from "./CandidateComparison";
import { db } from "../../firebase";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router";

interface MunicipalStatsProps {
  region: string;
  province: string;
  district: string;
  municipal: string;
  position: string;
}

const MunicipalStats: React.FC<MunicipalStatsProps> = ({ region, province, district, municipal, position }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tabulation, setTabulation] = useState<Tabulation>();
  const [barangayTabulations, setBarangayTabulations] = useState<Tabulation[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);

  const fetchMunicipalTabulation = async (user: User) => {
    const docSnap = await getDoc(doc(db, `tabulationsV2/${user.dashboard}/regions/${region}/provinces/${province}/districts/${district}/cities/${municipal}`));
    setTabulation(docSnap.data() as Tabulation);

    const barangaysSnap = await getDocs(collection(db, `tabulationsV2/${user.dashboard}/regions/${region}/provinces/${province}/districts/${district}/cities/${municipal}/barangays`));
    setBarangayTabulations(barangaysSnap.docs.map((d) => d.data() as Tabulation));
  };

  const fetchProvincialCandidates = async (): Promise<ProvincialCandidate[]> => {
    const candidatesRef = collection(db, "candidates");
    const q = query(candidatesRef, 
      where("type", "==", "provincial"),
      where("province", "==", province),
      where("position", "==", position),
      orderBy("name", "asc")
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as ProvincialCandidate);
  };  

  const fetchMunicipalCandidates = async (): Promise<MunicipalCandidate[]> => {
    const candidatesRef = collection(db, "candidates");
    const q = query(candidatesRef, 
      where("type", "==", "municipal"),
      where("province", "==", province),
      where("city_municipality", "==", municipal),
      where("position", "==", position),
      orderBy("name", "asc")
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as MunicipalCandidate);
  }

  const fetchTabulationAndCandidates = async (user: User) => {
    await fetchMunicipalTabulation(user);
    
    const provincialCandidates = await fetchProvincialCandidates();
    const municipalCandidates = await fetchMunicipalCandidates();
    setCandidates([...provincialCandidates, ...municipalCandidates]);
  }

  useEffect(() => {
    if (user && province && municipal) {
      fetchTabulationAndCandidates(user);
    }
  }, [user, province, municipal]);
  
  const onClick = (tabulation: BarangayTabulation, position: string) => {
    navigate(`/dashboard/candidateStats?type=${tabulation.type}&position=${position}&region=${tabulation.region}&province=${tabulation.province}&district=${tabulation.district}&municipal=${tabulation.city_municipality}&barangay=${tabulation.barangay}`);
  };
  
  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom align="center">
          {position} (Municipal Level)
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          { tabulation && (
            <CandidateComparison
              tabulation={tabulation}
              title={`${municipal}, ${province}`}
              position={position}
              candidates={candidates}
            />
          )}
        </Grid>
        { barangayTabulations.map((barangayTabulation) => (
          <Grid item xs={12} md={6} key={barangayTabulation.id}>
            <CandidateComparison
              tabulation={barangayTabulation}
              title={barangayTabulation.id}
              position={position}
              candidates={candidates}
              onClick={() => onClick(barangayTabulation as BarangayTabulation, position)}
            />
          </Grid>
        )) }
      </Grid>
    </Box>
  );
};

export default MunicipalStats;
